import { useEffect, useRef } from 'react';

const WHATSAPP_URL = 'https://wa.me/573228000501';

export default function HeroSection() {
  const perfumeRef = useRef<HTMLDivElement>(null);
  const modaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-4');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (perfumeRef.current) observer.observe(perfumeRef.current);
    if (modaRef.current) observer.observe(modaRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full h-screen flex flex-col md:flex-row overflow-hidden">
      {/* Perfumes Side */}
      <div
        className="relative w-full md:w-1/2 h-1/2 md:h-full overflow-hidden group cursor-pointer"
        onClick={() => scrollToSection('perfumes')}
      >
        <img
          src="https://storage.readdy-site.link/project_files/50551f36-9f7f-4435-8b77-175c3ce034c1/360340af-9f29-4b98-901a-d617960c64b8_creed.jpg?v=9d85e7324b699b7b6e080273ceff0888"
          alt="Colección de Perfumes"
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20 transition-all duration-700 group-hover:from-black/60 group-hover:via-black/20" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left" />
        <div
          ref={perfumeRef}
          className="relative h-full flex flex-col items-center justify-end pb-16 md:pb-20 px-8 text-center z-10 opacity-0 translate-y-4 transition-all duration-1000"
        >
          <span className="inline-block font-sans text-[10px] md:text-xs tracking-[0.3em] text-gold uppercase mb-4 opacity-90">
            Colección Exclusiva
          </span>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight text-shadow-luxury">
            Perfumes
          </h2>
          <p className="font-sans text-sm md:text-base text-white/80 mb-8 max-w-xs leading-relaxed font-light tracking-wide">
            Fragancias elegantes para&nbsp; cualquier ocasión.
          </p>
          <button
            className="group/btn relative overflow-hidden px-8 py-3 border border-gold text-gold font-sans text-xs tracking-[0.2em] uppercase font-medium hover:text-charcoal transition-colors duration-500 whitespace-nowrap cursor-pointer"
            onClick={(e) => { e.stopPropagation(); scrollToSection('perfumes'); }}
          >
            <span className="absolute inset-0 bg-gold translate-y-full group-hover/btn:translate-y-0 transition-transform duration-500 ease-out" />
            <span className="relative z-10">Ver Colección</span>
          </button>
        </div>
      </div>

      {/* Divider Desktop */}
      <div className="hidden md:flex absolute left-1/2 top-0 bottom-0 w-px z-20 flex-col items-center justify-center">
        <div className="flex-1 w-px bg-gradient-to-b from-transparent via-gold/60 to-transparent" />
        <div className="w-8 h-8 rounded-full border border-gold/60 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <span className="text-gold text-[8px] font-sans tracking-widest">LS</span>
        </div>
        <div className="flex-1 w-px bg-gradient-to-b from-transparent via-gold/60 to-transparent" />
      </div>

      {/* Divider Mobile */}
      <div className="md:hidden absolute left-0 right-0 top-1/2 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent z-20" />

      {/* Moda Side */}
      <div
        className="relative w-full md:w-1/2 h-1/2 md:h-full overflow-hidden group cursor-pointer"
        onClick={() => scrollToSection('ropa')}
      >
        <img
          src="https://storage.readdy-site.link/project_files/50551f36-9f7f-4435-8b77-175c3ce034c1/63a5342d-dfa5-4547-a6a2-d1626b74a224_set-aurora.jpg?v=37fbef1e391af57adfea89f476905144"
          alt="Colección de Moda"
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20 transition-all duration-700 group-hover:from-black/60 group-hover:via-black/20" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/60 scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-right" />
        <div
          ref={modaRef}
          className="relative h-full flex flex-col items-center justify-end pb-16 md:pb-20 px-8 text-center z-10 opacity-0 translate-y-4 transition-all duration-1000 delay-200"
        >
          <span className="inline-block font-sans text-[10px] md:text-xs tracking-[0.3em] text-white/70 uppercase mb-4">
            Nueva Temporada
          </span>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight text-shadow-luxury">
            Moda
          </h2>
          <p className="font-sans text-sm md:text-base text-white/80 mb-8 max-w-xs leading-relaxed font-light tracking-wide">
            Prendas exclusivas que resaltan tu estilo
          </p>
          <button
            className="group/btn relative overflow-hidden px-8 py-3 border border-white/70 text-white font-sans text-xs tracking-[0.2em] uppercase font-medium hover:text-charcoal transition-colors duration-500 whitespace-nowrap cursor-pointer"
            onClick={(e) => { e.stopPropagation(); scrollToSection('ropa'); }}
          >
            <span className="absolute inset-0 bg-white translate-y-full group-hover/btn:translate-y-0 transition-transform duration-500 ease-out" />
            <span className="relative z-10">Ver Colección</span>
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2 opacity-60">
        <div className="w-px h-8 bg-gradient-to-b from-white to-transparent animate-pulse" />
      </div>

      {/* Floating WhatsApp CTA */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-6 right-6 z-30 hidden md:flex items-center gap-2 bg-[#25D366] text-white text-xs font-sans font-semibold px-4 py-2 rounded-full shadow-lg hover:bg-[#20BA5A] transition-colors whitespace-nowrap"
      >
        <i className="ri-whatsapp-fill text-sm" />
        Escríbenos
      </a>
    </section>
  );
}
